import React, { useContext } from "react";
import { StoreCategory } from "tsi-common-react/src/api/retail";
import { updateStoreFilters } from "tsi-common-react/src/apps/retail/reducers";
import { IProps as IModalProps } from "tsi-common-react/src/components/Modal/index";
import {
    PopperOptions,
    UncontrolledPopover,
} from "tsi-common-react/src/components/Popover/index";
import { useMountEffect } from "tsi-common-react/src/hooks/useMountEffect";
import { isoMeters, isoWebPageURL } from "tsi-common-react/src/models/nominals";
import { useAppDispatch } from "tsi-common-react/src/store/reducers";
import { getCurrentLocale } from "tsi-common-react/src/utils/ajax";
import { getReactivatedContext } from "tsi-common-react/src/utils/environment";
import { usePageURL } from "tsi-common-react/src/utils/urls";
import { jt, t } from "ttag";

import { Context } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import { QuickSearchItem } from "../models";
import { LocatorQuickSearch as LocatorQuickSearchComponent } from "./LocatorQuickSearchContent";

const flagshipTooltipText = t`Choose from the widest assortment of genuine
    Tempur-Pedic products to discover your perfect bed with assistance from our
    highly experienced sleep experts.`;
const eliteTooltipText = t`Tempur-Pedic Elite Retailers offer a broad
    selection of Tempur-Pedic mattresses and adjustable bases. They are
    recognized for their comprehensive product knowledge, extraordinary
    service, and high level of customer satisfaction.`;

const popperOptions: PopperOptions = {
    modifiers: [
        {
            name: "offset",
            options: {
                offset: [0, 16],
            },
        },
        {
            name: "flip",
            options: {
                fallbackPlacements: ["top", "bottom"],
            },
        },
    ],
    placement: "bottom",
};

const modalProps: Partial<IModalProps> = {
    bodyOpenClassName: "tooltip-component__container",
    overlayClassName: "tooltip-component__overlay",
    className: "tooltip-component",
    contentLabel: t`More Info`,
    style: {
        overlay: {
            backgroundColor: "rgba(255, 255, 255, 0)",
        },
        content: {
            border: "none",
            boxShadow: "none",
        },
    },
};

const buildItems = (
    langCode: string,
    staticURL: string,
    retailerHref: string,
    flagshipHref: string,
): QuickSearchItem[] => {
    const isFrench = langCode.startsWith("fr");
    // French uses Unicode superscripts (ᴹᴰ), not <sup>.
    const trademarkMark = isFrench ? (
        "ᴹᴰ"
    ) : (
        <sup key="locator-quicksearch-flagship-title-sup">®</sup>
    );

    return [
        {
            id: "flagship",
            title: <>{jt`Tempur-Pedic${trademarkMark} Stores`}</>,
            descr: richT(t`<popover>Tempur-Pedic Stores</popover> near you.`, {
                popover: (children) => (
                    <UncontrolledPopover
                        triggerContent={children}
                        popperOptions={popperOptions}
                        modalProps={modalProps}
                        showArrow={true}
                    >
                        {flagshipTooltipText}
                    </UncontrolledPopover>
                ),
            }),
            labelClassName: "quicksearch__nav-button--tpstore",
            imgSrc: `${staticURL}img/locator/tabnav-flagship.jpg`,
            imgAlt: t`Tempur-Pedic Stores`,
            href: flagshipHref,
            defaultFilterState: {
                limit: 0,
                distance: isoMeters.wrap(0),
                category: StoreCategory.FLAGSHIP,
                retailerName: [],
            },
        },
        {
            id: "closest-5",
            title: t`Closest 5 Retailers`,
            descr: t`Closest five retailers based on your location.`,
            labelClassName: "quicksearch__nav-button--closest",
            imgSrc: `${staticURL}img/locator/tabnav-closest5.png`,
            imgAlt: t`Closest 5 Retailers`,
            href: `${retailerHref}#closest-5`,
            defaultFilterState: {
                limit: 5,
                category: null,
                retailerName: [],
            },
        },
        {
            id: "elite",
            title: t`Elite Retailers`,
            descr: richT(
                t`Find an <popover>Elite</popover> retailer near you.`,
                {
                    popover: (children) => (
                        <UncontrolledPopover
                            triggerContent={children}
                            popperOptions={popperOptions}
                            modalProps={modalProps}
                            showArrow={true}
                        >
                            {eliteTooltipText}
                        </UncontrolledPopover>
                    ),
                },
            ),
            labelClassName: "quicksearch__nav-button--elite",
            imgSrc: `${staticURL}img/locator/tabnav-elite.png`,
            imgAlt: t`Elite Retailers`,
            href: `${retailerHref}#elite`,
            defaultFilterState: {
                limit: 0,
                category: StoreCategory.ELITE,
                retailerName: [],
            },
        },
        {
            id: "all",
            title: t`All Retailers`,
            descr: t`All retailers in your area.`,
            labelClassName: "quicksearch__nav-button--all",
            imgSrc: `${staticURL}img/locator/tabnav-all.jpg`,
            imgAlt: t`All Retailers`,
            href: `${retailerHref}#all`,
            defaultFilterState: {
                limit: 0,
                category: null,
                retailerName: [],
            },
        },
    ];
};

type LocatorItemOptions = {
    langCode: string;
    staticURL: string;
    retailerHref: string;
    flagshipHref: string;
    hideElite?: boolean;
    hideTpStores?: boolean;
};

const getItems = ({
    langCode,
    staticURL,
    retailerHref,
    flagshipHref,
    hideElite,
    hideTpStores,
}: LocatorItemOptions): QuickSearchItem[] => {
    let filteredItems = buildItems(
        langCode,
        staticURL,
        retailerHref,
        flagshipHref,
    );

    if (hideElite) {
        filteredItems = filteredItems.filter((item) => item.id !== "elite");
    }

    if (hideTpStores) {
        filteredItems = filteredItems.filter((item) => item.id !== "flagship");
    }

    return filteredItems;
};

const handleHashChange = (
    dispatch: ReturnType<typeof useAppDispatch>,
    options: LocatorItemOptions,
    setHash: (hash: string) => void,
): void => {
    const window = getWindow();
    if (!window) {
        return;
    }
    const hash = window.location.hash.replace("#", "") || "closest-5";
    setHash(hash);
    const filteredItems = getItems(options);
    const selectedItem = filteredItems.find((item) => item.id === hash);
    if (selectedItem) {
        dispatch(updateStoreFilters(selectedItem.defaultFilterState));
    }
};

export const LocatorQuickSearch = (props: {
    selectedItem?: string;
    hideElite?: boolean;
    hideTpStores?: boolean;
}): React.ReactNode => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const context = useContext(Context) || getReactivatedContext();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const staticURL = context?.STATIC_URL ?? "/static/";
    const dispatch = useAppDispatch();
    const [hash, setHash] = React.useState("");

    const langCode = context?.i18n?.language?.code || getCurrentLocale();
    const findARetailerURL = usePageURL("find_a_retailer");
    const flagshipStoresURL = usePageURL("tempur_pedic_stores");
    // Prefer empty over unprefixed EN paths (FR→EN 302 when usePageURL is null).
    const retailerHref = findARetailerURL
        ? isoWebPageURL.unwrap(findARetailerURL)
        : "";
    const flagshipHref = flagshipStoresURL
        ? isoWebPageURL.unwrap(flagshipStoresURL)
        : "";

    const itemOptions: LocatorItemOptions = {
        langCode,
        staticURL,
        retailerHref,
        flagshipHref,
        hideElite: props.hideElite,
        hideTpStores: props.hideTpStores,
    };

    useMountEffect(() => {
        const onHashChange = () => {
            handleHashChange(dispatch, itemOptions, setHash);
        };

        const window = getWindow();
        if (!window) {
            return;
        }

        onHashChange();
        window.addEventListener("hashchange", onHashChange);

        return () => {
            window.removeEventListener("hashchange", onHashChange);
        };
    });

    const selectedItem = props.selectedItem || hash.replace("#", "");
    return (
        <LocatorQuickSearchComponent
            items={getItems(itemOptions)}
            selectedItem={selectedItem}
        />
    );
};
